import React, {useDeferredValue, useEffect, useRef, useState} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {
	MdAdd,
	MdCheckCircle,
	MdDeleteForever,
	MdInfo,
	MdList,
	MdLocationOn,
	MdSearch,
} from 'react-icons/md';
import {Link as ReactLink} from 'react-router-dom';
import BodyContainer from '../../../components/BodyContainer';
import MobilePopover from '../../../components/MobilePopover';
import SidePanel from '../../../components/SidePanel';
import TouchableHover from '../../../components/TouchableHover';
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormLabel,
	Heading,
	HStack,
	Icon,
	Input,
	InputGroup,
	InputLeftAddon,
	Progress,
	Stack,
	Table,
	Tag,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	VStack,
	useOutsideClick,
	useToast,
} from 'components/ui/chakra';
import {Glum} from 'puddleglum';
import LocationController from 'puddleglum/api/providers/LocationController';
import PriceController from 'puddleglum/api/providers/PriceController';
import ProviderTreatmentGroupSearchController from 'puddleglum/api/providers/ProviderTreatmentGroupSearchController';
import ProviderTreatmentSearchController from 'puddleglum/api/providers/ProviderTreatmentSearchController';
import {PaginatedResponse} from 'puddleglum/utils';

type ServiceTargetKind = 'treatment' | 'treatmentGroup';

type ServiceTarget = {
	id: number;
	kind: ServiceTargetKind;
	key: string;
	title: string;
	subtitle: string;
};

type StagedService = {
	id: string;
	target: ServiceTarget;
	displayName: string;
	price: string;
	locationIds: number[];
	existingPriceId: number | null;
};

type ServiceValidation = {
	hasDisplayName: boolean;
	hasValidPrice: boolean;
	hasLocations: boolean;
	parsedPrice: number | null;
	isReady: boolean;
};

interface QuickServiceBuilderProps {
	isOpen: boolean;
	onClose: () => void;
}

const makeTreatmentTarget = (treatment: Glum.Models.Treatment): ServiceTarget => ({
	id: treatment.id,
	kind: 'treatment',
	key: `treatment:${treatment.id}`,
	title: treatment.treatment,
	subtitle: treatment.treatment_code,
});

const makeTreatmentGroupTarget = (treatmentGroup: Glum.Models.TreatmentGroup): ServiceTarget => ({
	id: treatmentGroup.id,
	kind: 'treatmentGroup',
	key: `treatmentGroup:${treatmentGroup.id}`,
	title: treatmentGroup.name,
	subtitle: `GROUP-${treatmentGroup.id}`,
});

const getExistingLocationIds = (price: Glum.Models.Price): number[] =>
	(price.treatment?.locations ?? price.treatment_group?.locations ?? []).map(
		(location) => location.id,
	);

const getLocationNames = (
	locationIds: number[],
	locationOptions: Glum.Models.Location[],
): string[] =>
	locationIds
		.map((locationId) => locationOptions.find((location) => location.id === locationId)?.name)
		.filter((name): name is string => !!name);

const summarizeLocations = (
	locationIds: number[],
	locationOptions: Glum.Models.Location[],
): string => {
	const names = getLocationNames(locationIds, locationOptions);

	if (names.length === 0) {
		return 'No locations assigned';
	}

	if (names.length <= 2) {
		return names.join(', ');
	}

	return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
};

const validateService = (service: StagedService): ServiceValidation => {
	const trimmedPrice = service.price.trim();
	const parsedPrice = trimmedPrice.length === 0 ? null : Number(trimmedPrice);
	const hasDisplayName = service.displayName.trim().length > 0;
	const hasValidPrice = parsedPrice !== null && Number.isFinite(parsedPrice) && parsedPrice >= 0;
	const hasLocations = service.locationIds.length > 0;

	return {
		hasDisplayName,
		hasValidPrice,
		hasLocations,
		parsedPrice: hasValidPrice ? parsedPrice : null,
		isReady: hasDisplayName && hasValidPrice && hasLocations,
	};
};

const getStatusLabel = (service: StagedService, validation: ServiceValidation): string => {
	const notes: string[] = [];

	if (service.existingPriceId !== null) {
		notes.push('This selection already has a saved price. Saving this row will update it.');
	}

	if (!validation.hasDisplayName) {
		notes.push('Add a display name.');
	}

	if (!validation.hasValidPrice) {
		notes.push('Enter a valid non-negative price.');
	}

	if (!validation.hasLocations) {
		notes.push('Choose at least one location.');
	}

	if (notes.length > 0) {
		return notes.join(' ');
	}

	return service.existingPriceId !== null
		? 'This row is ready and will update the existing service when saved.'
		: 'This row is ready to save.';
};

const QuickServiceBuilder = ({isOpen, onClose}: QuickServiceBuilderProps) => {
	const queryClient = useQueryClient();
	const toast = useToast();
	const searchRef = useRef<HTMLDivElement>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [stagedServices, setStagedServices] = useState<StagedService[]>([]);
	const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
	const [isPublishing, setIsPublishing] = useState(false);
	const deferredSearchTerm = useDeferredValue(searchTerm.trim());

	const {data: locations, isLoading: areLocationsLoading} = useQuery<
		PaginatedResponse<Glum.Models.Location>
	>(
		['provider-quick-service-builder', 'locations'],
		async () => {
			const reply = await LocationController.index({
				page: 1,
				per_page: 1000,
				search: '',
			});

			return reply.data;
		},
		{
			enabled: isOpen,
		},
	);

	const {data: existingPrices, isLoading: arePricesLoading} = useQuery<
		PaginatedResponse<Glum.Models.Price>
	>(
		['provider-quick-service-builder', 'prices'],
		async () => {
			const reply = await PriceController.index({
				page: 1,
				per_page: 1000,
				search: '',
				active_only: false,
			});

			return reply.data;
		},
		{
			enabled: isOpen,
		},
	);

	const {data: searchResults = [], isFetching: isSearchingTargets} = useQuery<ServiceTarget[]>(
		['provider-quick-service-builder', 'mixed-search', deferredSearchTerm],
		async () => {
			const [treatmentsReply, treatmentGroupsReply] = await Promise.all([
				ProviderTreatmentSearchController.search({
					search: deferredSearchTerm,
				}),
				ProviderTreatmentGroupSearchController.search({
					search: deferredSearchTerm,
				}),
			]);

			return [
				...treatmentsReply.data.map(makeTreatmentTarget),
				...treatmentGroupsReply.data.map(makeTreatmentGroupTarget),
			].sort((left, right) => {
				const titleComparison = left.title.localeCompare(right.title);

				if (titleComparison !== 0) {
					return titleComparison;
				}

				return left.kind.localeCompare(right.kind);
			});
		},
		{
			enabled: isOpen && deferredSearchTerm.length >= 2,
		},
	);

	const locationOptions = locations?.data ?? [];
	const hasLocations = locationOptions.length > 0;
	const isLoading = areLocationsLoading || arePricesLoading;
	const canSearch = deferredSearchTerm.length >= 2;

	const existingPriceMap = (existingPrices?.data ?? []).reduce((priceMap, price) => {
		if (price.treatment_id) {
			priceMap.set(`treatment:${price.treatment_id}`, price);
		}

		if (price.treatment_group_id) {
			priceMap.set(`treatmentGroup:${price.treatment_group_id}`, price);
		}

		return priceMap;
	}, new Map<string, Glum.Models.Price>());

	const canPublish =
		stagedServices.length > 0 &&
		stagedServices.every((service) => validateService(service).isReady);

	useOutsideClick({
		ref: searchRef,
		handler: () => setIsSearchOpen(false),
	});

	useEffect(() => {
		if (!isOpen) {
			setSearchTerm('');
			setIsSearchOpen(false);
			setStagedServices([]);
			setHighlightedRowId(null);
			setIsPublishing(false);
		}
	}, [isOpen]);

	useEffect(() => {
		let timeout: number | undefined;

		if (highlightedRowId) {
			timeout = window.setTimeout(() => {
				setHighlightedRowId(null);
			}, 1800);
		}

		return () => window.clearTimeout(timeout);
	}, [highlightedRowId]);

	const focusRow = (rowId: string) => {
		const rowElement = document.getElementById(`quick-service-row-${rowId}`);

		rowElement?.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
		});

		const firstInput = rowElement?.querySelector<HTMLInputElement>(
			'input[data-row-field="display-name"]',
		);

		firstInput?.focus();
		setHighlightedRowId(rowId);
	};

	const updateService = (serviceId: string, changes: Partial<StagedService>) => {
		setStagedServices((currentServices) =>
			currentServices.map((service) =>
				service.id === serviceId
					? {
							...service,
							...changes,
						}
					: service,
			),
		);
	};

	const toggleLocation = (service: StagedService, locationId: number) => {
		updateService(service.id, {
			locationIds: service.locationIds.includes(locationId)
				? service.locationIds.filter((id) => id !== locationId)
				: [...service.locationIds, locationId],
		});
	};

	const handleSelectTarget = (target: ServiceTarget) => {
		const existingRow = stagedServices.find((service) => service.id === target.key);

		setSearchTerm('');
		setIsSearchOpen(false);

		if (existingRow) {
			focusRow(existingRow.id);
			toast({
				title: 'Service already in sheet',
				description: 'Update the existing row instead of adding a duplicate.',
				status: 'info',
				duration: 4000,
				isClosable: true,
			});

			return;
		}

		const existingPrice = existingPriceMap.get(target.key);
		const nextService: StagedService = {
			id: target.key,
			target,
			displayName: existingPrice?.display_name?.trim() || target.title,
			price: existingPrice ? String(existingPrice.price) : '',
			locationIds: existingPrice ? getExistingLocationIds(existingPrice) : [],
			existingPriceId: existingPrice?.id ?? null,
		};

		setStagedServices((currentServices) => [...currentServices, nextService]);

		if (existingPrice) {
			toast({
				title: 'Existing price found',
				description: 'This row was added so you can update the service you already priced.',
				status: 'info',
				duration: 4000,
				isClosable: true,
			});
		}
	};

	const removeService = (serviceId: string) => {
		setStagedServices((currentServices) =>
			currentServices.filter((service) => service.id !== serviceId),
		);

		if (highlightedRowId === serviceId) {
			setHighlightedRowId(null);
		}
	};

	const publishServices = async () => {
		if (!canPublish) {
			toast({
				title: 'Resolve the rows that still need attention',
				description:
					'Each row needs a display name, a valid non-negative price, and at least one location.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});

			return;
		}

		setIsPublishing(true);

		try {
			const reply = await PriceController.batchStore({
				items: stagedServices.map((service) => ({
					treatment_id: service.target.kind === 'treatment' ? service.target.id : null,
					treatment_group_id:
						service.target.kind === 'treatmentGroup' ? service.target.id : null,
					display_name: service.displayName.trim(),
					price: validateService(service).parsedPrice ?? 0,
					location_ids: service.locationIds,
				})),
			});

			queryClient.invalidateQueries(['treatments']);
			queryClient.invalidateQueries(['provider-quick-service-builder', 'prices']);

			toast({
				title: 'Services saved',
				description: `${reply.data.created_count} created, ${reply.data.updated_count} updated.`,
				status: 'success',
				duration: 5000,
				isClosable: true,
			});

			onClose();
		} catch (error) {
			toast({
				title: 'Unable to save services',
				description: 'Review the rows in the sheet and try again.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		} finally {
			setIsPublishing(false);
		}
	};

	return (
		<SidePanel
			isOpen={isOpen}
			onClose={onClose}
			size='full'
			title='What services does your organization offer?'
		>
			<VStack align='stretch' spacing={4}>
				{isLoading && <Progress size='xs' isIndeterminate />}

				<BodyContainer p={5}>
					<Stack spacing={5}>
						<Stack
							direction={{base: 'column', xl: 'row'}}
							justify='space-between'
							align={{base: 'stretch', xl: 'flex-end'}}
							gap={4}
						>
							<Box flex={1} maxW='4xl' position='relative' ref={searchRef}>
								<FormControl>
									<FormLabel color='gray.500'>Add a service</FormLabel>
									<InputGroup w='full'>
										<InputLeftAddon>
											<MdSearch />
										</InputLeftAddon>
										<Input
											placeholder='Search treatments and treatment groups'
											value={searchTerm}
											onChange={(event) => {
												setSearchTerm(event.target.value);
												setIsSearchOpen(true);
											}}
											onFocus={() => setIsSearchOpen(true)}
											isDisabled={isLoading}
										/>
									</InputGroup>
								</FormControl>
								<Text color='gray.500' fontSize='sm' mt={2}>
									Type at least 2 characters, then choose a result to add it to
									the sheet.
								</Text>

								{isSearchOpen && canSearch && (
									<Box
										bg='white'
										border='1px solid'
										borderColor='gray.200'
										borderRadius='md'
										boxShadow='md'
										left={0}
										maxH='20rem'
										mt={2}
										overflowY='auto'
										position='absolute'
										right={0}
										top='100%'
										zIndex={5}
									>
										{isSearchingTargets && (
											<Box p={4}>
												<Text color='gray.500' fontSize='sm'>
													Searching...
												</Text>
											</Box>
										)}

										{!isSearchingTargets && searchResults.length === 0 && (
											<Box p={4}>
												<Text color='gray.500' fontSize='sm'>
													No matching services found.
												</Text>
											</Box>
										)}

										{searchResults.map((result) => (
											<Button
												key={result.key}
												variant='ghost'
												w='full'
												justifyContent='space-between'
												onClick={() => handleSelectTarget(result)}
											>
												<HStack
													justify='space-between'
													w='full'
													align='flex-start'
												>
													<Box minW={0} textAlign='left'>
														<HStack spacing={2} mb={1} flexWrap='wrap'>
															<Tag
																colorScheme={
																	result.kind === 'treatment'
																		? 'blue'
																		: 'purple'
																}
															>
																{result.kind === 'treatment'
																	? 'Treatment'
																	: 'Group'}
															</Tag>
															<Tag colorScheme='gray'>
																{result.subtitle}
															</Tag>
														</HStack>
														<Text noOfLines={1}>{result.title}</Text>
													</Box>
													{result.kind === 'treatment' ? (
														<MdList />
													) : (
														<MdLocationOn />
													)}
												</HStack>
											</Button>
										))}
									</Box>
								)}
							</Box>

							<Button
								alignSelf={{base: 'flex-start', xl: 'flex-end'}}
								as={ReactLink}
								leftIcon={<MdAdd />}
								to='/services-and-prices'
								variant='outline'
							>
								Open Full Editor
							</Button>
						</Stack>

						{!hasLocations && !isLoading && (
							<Box
								bg='orange.50'
								border='1px solid'
								borderColor='orange.200'
								borderRadius='lg'
								p={4}
							>
								<Stack
									direction={{base: 'column', lg: 'row'}}
									justify='space-between'
									align={{base: 'flex-start', lg: 'center'}}
									gap={3}
								>
									<Box>
										<Text color='orange.700' fontWeight='semibold'>
											You can stage rows now, but you need at least one
											location before you can save searchable services.
										</Text>
										<Text color='orange.700' fontSize='sm' mt={1}>
											Add a location, then come back to finish the sheet.
										</Text>
									</Box>
									<Button as={ReactLink} colorScheme='blue' to='/locations'>
										Add Locations
									</Button>
								</Stack>
							</Box>
						)}

						{stagedServices.length === 0 ? (
							<Box
								bg='gray.50'
								border='1px dashed'
								borderColor='gray.200'
								borderRadius='lg'
								p={8}
								textAlign='center'
							>
								<Heading size='sm'>Your sheet is empty</Heading>
								<Text color='gray.600' mt={2}>
									Search for a treatment or treatment group above to add your
									first row.
								</Text>
							</Box>
						) : (
							<Box overflowX='auto'>
								<Table minW='70rem'>
									<Thead>
										<Tr>
											<Th>Service</Th>
											<Th>Display Name</Th>
											<Th>Price</Th>
											<Th>Locations</Th>
											<Th>Status</Th>
											<Th textAlign='right'>Actions</Th>
										</Tr>
									</Thead>
									<Tbody>
										{stagedServices.map((service) => {
											const validation = validateService(service);
											const locationNames = getLocationNames(
												service.locationIds,
												locationOptions,
											);

											return (
												<Tr
													key={service.id}
													id={`quick-service-row-${service.id}`}
													bg={
														highlightedRowId === service.id
															? 'blue.50'
															: 'transparent'
													}
													transition='background-color 0.2s ease'
												>
													<Td verticalAlign='top'>
														<Stack spacing={2} minW='16rem'>
															<HStack spacing={2} flexWrap='wrap'>
																<Tag
																	colorScheme={
																		service.target.kind ===
																		'treatment'
																			? 'blue'
																			: 'purple'
																	}
																>
																	{service.target.kind ===
																	'treatment'
																		? 'Treatment'
																		: 'Group'}
																</Tag>
																<Tag colorScheme='gray'>
																	{service.target.subtitle}
																</Tag>
															</HStack>
															<Text fontWeight='semibold'>
																{service.target.title}
															</Text>
														</Stack>
													</Td>
													<Td verticalAlign='top'>
														<Input
															data-row-field='display-name'
															value={service.displayName}
															onChange={(event) =>
																updateService(service.id, {
																	displayName: event.target.value,
																})
															}
															placeholder='What should patients see?'
															w='18rem'
															borderColor={
																validation.hasDisplayName
																	? undefined
																	: 'red.300'
															}
														/>
													</Td>
													<Td verticalAlign='top'>
														<Stack spacing={2} minW='10rem'>
															<InputGroup maxW='10rem'>
																<InputLeftAddon>$</InputLeftAddon>
																<Input
																	type='number'
																	step='0.01'
																	min='0'
																	value={service.price}
																	onChange={(event) =>
																		updateService(service.id, {
																			price: event.target
																				.value,
																		})
																	}
																	placeholder='0.00'
																	borderColor={
																		validation.hasValidPrice ||
																		service.price.trim()
																			.length === 0
																			? undefined
																			: 'red.300'
																	}
																/>
															</InputGroup>
															<Text color='gray.500' fontSize='xs'>
																Use $0.00 if this service is free.
															</Text>
														</Stack>
													</Td>
													<Td verticalAlign='top'>
														<Stack spacing={2} minW='16rem'>
															<MobilePopover
																trigger='click'
																triggerElement={
																	<Button
																		leftIcon={<MdLocationOn />}
																		size='sm'
																		variant='outline'
																	>
																		{locationNames.length === 0
																			? hasLocations
																				? 'Choose locations'
																				: 'No locations available'
																			: locationNames.length ===
																				  1
																				? locationNames[0]
																				: `${locationNames.length} locations`}
																	</Button>
																}
															>
																{hasLocations ? (
																	<Stack spacing={4}>
																		<HStack
																			justify='space-between'
																			flexWrap='wrap'
																		>
																			<Text fontWeight='semibold'>
																				Locations
																			</Text>
																			<HStack spacing={2}>
																				<Button
																					size='xs'
																					variant='ghost'
																					onClick={() =>
																						updateService(
																							service.id,
																							{
																								locationIds:
																									locationOptions.map(
																										(
																											location,
																										) =>
																											location.id,
																									),
																							},
																						)
																					}
																				>
																					Select all
																				</Button>
																				<Button
																					size='xs'
																					variant='ghost'
																					onClick={() =>
																						updateService(
																							service.id,
																							{
																								locationIds:
																									[],
																							},
																						)
																					}
																					isDisabled={
																						service
																							.locationIds
																							.length ===
																						0
																					}
																				>
																					Clear
																				</Button>
																			</HStack>
																		</HStack>
																		<Stack
																			maxH='18rem'
																			overflowY='auto'
																			pr={1}
																			spacing={2}
																		>
																			{locationOptions.map(
																				(location) => (
																					<Checkbox
																						key={
																							location.id
																						}
																						isChecked={service.locationIds.includes(
																							location.id,
																						)}
																						onChange={() =>
																							toggleLocation(
																								service,
																								location.id,
																							)
																						}
																					>
																						{
																							location.name
																						}
																					</Checkbox>
																				),
																			)}
																		</Stack>
																	</Stack>
																) : (
																	<Stack spacing={3}>
																		<Text
																			color='gray.600'
																			fontSize='sm'
																		>
																			Add a location first,
																			then come back to assign
																			it to this service.
																		</Text>
																		<Button
																			as={ReactLink}
																			colorScheme='blue'
																			size='sm'
																			to='/locations'
																		>
																			Add Locations
																		</Button>
																	</Stack>
																)}
															</MobilePopover>
															<Text
																color='gray.500'
																fontSize='xs'
																noOfLines={2}
															>
																{summarizeLocations(
																	service.locationIds,
																	locationOptions,
																)}
															</Text>
														</Stack>
													</Td>
													<Td verticalAlign='top'>
														<TouchableHover
															label={getStatusLabel(
																service,
																validation,
															)}
														>
															<span>
																<Icon
																	as={
																		validation.isReady &&
																		service.existingPriceId ===
																			null
																			? MdCheckCircle
																			: MdInfo
																	}
																	color={
																		!validation.isReady
																			? 'orange.500'
																			: service.existingPriceId !==
																				  null
																				? 'blue.500'
																				: 'green.500'
																	}
																	boxSize={5}
																	cursor='help'
																/>
															</span>
														</TouchableHover>
													</Td>
													<Td verticalAlign='top' textAlign='right'>
														<Button
															colorScheme='red'
															leftIcon={<MdDeleteForever />}
															size='sm'
															variant='ghost'
															onClick={() =>
																removeService(service.id)
															}
														>
															Remove
														</Button>
													</Td>
												</Tr>
											);
										})}
									</Tbody>
								</Table>
							</Box>
						)}

						<HStack justify='flex-end' flexWrap='wrap' gap={4}>
							<Button
								colorScheme='blue'
								isDisabled={!canPublish}
								isLoading={isPublishing}
								loadingText='Saving'
								onClick={publishServices}
							>
								Save Services
							</Button>
						</HStack>
					</Stack>
				</BodyContainer>
			</VStack>
		</SidePanel>
	);
};

export default QuickServiceBuilder;
